import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link2, Loader2, Check, Unlink, RefreshCw, AlertTriangle, Lock } from 'lucide-react';

/**
 * LinkedIn connect — Sprint 69 (1/N). The real app has ONLY "Sign In with
 * LinkedIn using OpenID Connect" + "Share on LinkedIn" (w_member_social)
 * approved — never organization posting/comments (needs Community
 * Management API), never a real inbox (needs Communications API + partner
 * approval), never Ads (separate app, linkedin_ads_oauth, not configured).
 * Those three show as explicitly disabled-with-reason, never hidden as if
 * they didn't exist and never silently offered.
 * @param {object} props
 * @param {function} [props.onConnected]
 * @param {boolean} [props.compact]
 */
const CAPABILITY_LABELS = {
  identity: 'Identidad verificada',
  personal_posting: 'Publicar en tu perfil personal',
  organization_posting: 'Publicar/comentar como organización (página de empresa)',
  messages: 'Bandeja de mensajes real',
  ads: 'Anuncios de LinkedIn',
};

const REASON_LABELS = {
  missing_authorization: 'Falta autorización — reconecta LinkedIn',
  missing_platform_approval: 'Falta aprobación de plataforma (LinkedIn no ha aprobado este producto para la app)',
  separate_app_not_configured: 'Requiere la app separada "LinkedIn Ads" en Super Admin > Infraestructura, aún sin configurar',
};

export default function LinkedInLoginConnect({ onConnected, compact = false }) {
  const [status, setStatus] = useState('loading'); // loading | not_connected | connected | error
  const [conn, setConn] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [reauthorizing, setReauthorizing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const { data } = await api.get('/tenant/oauth/linkedin/account');
      if (data.connected) {
        setConn(data);
        setStatus('connected');
      } else {
        setConn(null);
        setStatus('not_connected');
      }
    } catch (e) {
      setStatus('error');
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'linkedin') {
      if (params.get('connected') === '1') {
        load().then(() => onConnected?.());
      } else {
        const reason = params.get('reason');
        toast.error(
          reason === 'linkedin_incomplete_auth'
            ? 'LinkedIn no pudo verificar tu identidad — vuelve a intentarlo.'
            : 'No se pudo completar la conexión con LinkedIn — vuelve a intentarlo.'
        );
        setStatus('not_connected');
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth'); url.searchParams.delete('connected'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/linkedin/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con LinkedIn');
      setRedirecting(false);
    }
  };

  const reauthorize = async () => {
    setReauthorizing(true);
    await connect();
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de LinkedIn?')) return;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/linkedin/disconnect');
      toast.success('LinkedIn desconectado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando tu cuenta de LinkedIn...</div>;
  }

  if (status === 'error') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <Button onClick={load} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  if (status === 'not_connected') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <p className="text-sm text-[#64748B] mb-3">
          Conecta tu cuenta personal de LinkedIn para verificar tu identidad y publicar en tu perfil (no en páginas
          de empresa). Comentarios, mensajes y anuncios de LinkedIn son pasos futuros separados — esta conexión
          todavía no los habilita.
        </p>
        <Button onClick={connect} disabled={redirecting} className="gap-2" data-testid="linkedin-connect-button">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Conectar LinkedIn
        </Button>
      </div>
    );
  }

  const expiresAt = conn?.expires_at ? new Date(conn.expires_at) : null;
  const needsReauth = conn?.status === 'expired' || conn?.status === 'expiring_soon';

  return (
    <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'} data-testid="linkedin-connected">
      {needsReauth && (
        <div className={`flex flex-col gap-2 border rounded-lg px-3 py-2 mb-3 text-xs ${conn.status === 'expired' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {conn.status === 'expired'
                ? 'Tu conexión de LinkedIn venció. LinkedIn no siempre concede un token de renovación automática — reautoriza para seguir publicando.'
                : `Tu conexión de LinkedIn vence pronto (${expiresAt ? expiresAt.toLocaleDateString() : 'en unos días'}). Reautoriza antes de que expire para no perder la publicación automática.`}
            </span>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs self-start" onClick={reauthorize}
            disabled={reauthorizing || redirecting} data-testid="linkedin-reauthorize-button">
            {(reauthorizing || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Reautorizar LinkedIn
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 mb-3 bg-[#F8FAFC]">
        <div className="flex items-center gap-2">
          {conn?.picture && <img src={conn.picture} alt="" className="w-6 h-6 rounded-full" />}
          <div>
            <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600" /> {conn?.name || 'Conectado'}
            </p>
            <p className="text-[11px] text-[#94A3B8]">
              {expiresAt ? `Vence el ${expiresAt.toLocaleDateString()}` : 'Sin fecha de vencimiento registrada'}
              {conn?.has_refresh_token === false && ' — sin renovación automática, requiere reautorizar manualmente'}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          {!needsReauth && (
            <Button size="sm" variant="outline" onClick={reauthorize} disabled={reauthorizing || redirecting} title="Reautorizar">
              {(reauthorizing || redirecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={disconnect} disabled={disconnecting} title="Desconectar">
            {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
      <div className="border border-[#E2E8F0] rounded-lg px-3 py-2">
        <p className="text-xs font-semibold text-[#0F172A] mb-2">Qué puede hacer esta conexión hoy</p>
        <div className="space-y-1.5">
          {Object.entries(conn?.capabilities || {}).map(([key, cap]) => (
            <div key={key} className="flex items-start gap-2 text-xs">
              {cap.available
                ? <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                : <Lock className="w-3.5 h-3.5 text-[#CBD5E1] mt-0.5 shrink-0" />}
              <div>
                <span className={cap.available ? 'text-[#0F172A]' : 'text-[#94A3B8]'}>{CAPABILITY_LABELS[key] || key}</span>
                {!cap.available && cap.reason && (
                  <span className="text-[#CBD5E1]"> — {REASON_LABELS[cap.reason] || cap.reason}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
